/**
 * CreateDistrictMap - Ren presentational React-komponent
 * 
 * All kart-logik finns i useDistrictMap hook
 * Denna komponent hanterar bara JSX och rendering
 */

import React, { useCallback } from 'react';
import { withRouter } from 'react-router';
import classNames from 'classnames';
import withFetch from '../../hoc/withFetch';
import withAuthProvider from '../../hoc/withAuthProvider';
import SpinnerDots from '../spinner/dots';
import './create-district-map.scss';
import MapContent from './MapContent';
import MapToolbar from './MapToolbar';
import MapPanel from './map-panel/MapPanel';
import { useDistrictMap } from './hooks/use-district-map';
import useClassifications from './classifications/useClassifications';
import * as districtCache from './helpers/districts/district-cache';
import { applyDistrictFiltersAndCalculate } from './helpers/districts/sales-districts-operations';

function CreateDistrictMap(props) {
    const { state, handlers, mapUiLoading } = useDistrictMap(props);
    const hasAreas = (handlers.getSelectedAreas?.() || []).length > 0;
    const saleDistrictsVisible = districtCache.getAllDistrictsForScenario().some(district => district?.visible);
    
    // Hämta classifications en gång och dela mellan komponenter
    const scenarioId = props.match.params.scenarioId || null;
    const { data: classifications, isLoading: classificationsLoading, reload: reloadClassifications } = useClassifications(scenarioId, props.fetch);

    const handleClassificationsChanged = useCallback(async () => {
        const newClassifications = await reloadClassifications();
        if (scenarioId == null || newClassifications === undefined) {
            return;
        }

        districtCache.setClassifications(scenarioId, newClassifications);

        const allDistricts = districtCache.getAllDistrictsForScenario();
        for (const district of allDistricts) {
            if (!district?.id) {
                continue;
            }
            const { calculations } = applyDistrictFiltersAndCalculate(district, newClassifications);
            districtCache.set(district.id, {
                ...district,
                frequency: calculations.frequency,
                vistingTimeHours: calculations.vistingTimeHours
            });
        }
        districtCache.notifySubscribers();
    }, [reloadClassifications, scenarioId]);

    const onApiLoaded = useCallback((map, maps) => {
        handlers.onApiLoaded(map, maps);
    }, []);
    
    return (
        <div className={classNames({
            'create-district-map': true,
            'create-district-map--is-loading': mapUiLoading,
            'create-district-map--is-loading-markers': state.uiStates?.isLoadingMarkers
        })}>
            <SpinnerDots />
            <div className="create-district-map__map-container">
                <MapContent 
                    isLoadingMarkers={state.uiStates?.isLoadingMarkers}
                    onGoogleApiLoaded={onApiLoaded}
                />
                
                <MapToolbar 
                    // Drawing tools
                    drawPolygon={() => handlers.drawPolygon()}
                    drawCircle={() => handlers.drawCircle()}
                    stopDrawing={() => handlers.stopDrawing?.()}
                    toggleDrawHouse={(isToolActive) => handlers.toggleDrawHouse(isToolActive)}
                    undo={() => handlers.undo()}
                    undoIsActive={state.uiStates?.undoIsActive}
                    hasAreas={hasAreas}
                    isLoading={mapUiLoading}
                    
                    // Selection
                    selectionType={handlers.getSelectionType() || 'clear'}
                    hasSelectionType={(type) => handlers.hasSelectionType(type) || false}
                    onSelectionChange={handlers.handleSelectionChange}
                    
                    // Search
                    selectSearchedArea={async (searchObj) => await handlers.selectSearchedArea(searchObj)}
                    
                    // Map type
                    type={props.type}
                    
                    // Layer toggles
                    onToggleUnits={handlers.toggleUnits}
                    onToggleSaleDistrict={(e) => handlers.toggleSaleDistrict(e)}
                    saleDistrictsVisible={saleDistrictsVisible}
                    unitsVisible={state.uiStates.unitsVisibility}
                    
                    // Tools
                    onAddTools={(tools) => handlers.addTools(tools)}
                />
            </div>
            <MapPanel 
                state={state}
                mapUiLoading={mapUiLoading}
                handlers={handlers}
                match={props.match}
                type={props.type}
                classifications={classifications}
                classificationsLoading={classificationsLoading}
                reloadClassifications={handleClassificationsChanged}
                fetch={props.fetch}
            />
        </div>
    );
}

export default withAuthProvider(withFetch(withRouter(CreateDistrictMap)));
